import { Query } from "@apollo/client/react/components";
import { Button } from "@govlaunch/core";
import * as palette from "@govlaunch/palette";
import { Flex } from "@govlaunch/web";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import VendorPendingVerificationProjects from "~/lib/queries/VendorPendingVerificationProjects";
import VendorProjectsQuery from "~/lib/queries/VendorProjectsQuery";
import {
  IVendorPendingVerificationProjectsQuery,
  IVendorPendingVerificationProjectsQueryVariables,
} from "~/lib/queries/__generated__/VendorPendingVerificationProjects.generated";
import {
  IVendorProjectsQuery,
  IVendorProjectsQueryVariables,
} from "~/lib/queries/__generated__/VendorProjectsQuery.generated";
import { IVendorQuery } from "~/lib/queries/__generated__/VendorQuery.generated";
import CreateProjectModal from "~/src/components/project/CreateProjectModal";
import ProjectCards from "~/src/components/project/listing/ProjectCards";
import ProjectsListing from "~/src/components/project/listing/ProjectsListing";
import ShowMoreButtonWithIcon from "~/components/form/resources/ShowMoreButtonWithIcon";
import PenIcon from "~/components/icons/PenIcon";
import NoProjectsBox from "~/components/profile/NoProjectsBox";
import { Margin } from "~/components/spacings";
import UpgradeAccountBanner from "~/components/vendor/profile/UpgradeAccountBanner";
import NoProjectConnectionsBox from "~/components/project/NoProjectConnectionsBox";

interface IFullVendorProjects {
  vendor: NonNullable<IVendorQuery["company"]>;
  showUpgradeBanner?: boolean;
  showPlaceholder?: boolean;
}

interface IVendorProjects {
  vendor: NonNullable<IVendorQuery["company"]>;
  showPlaceholder?: boolean;
}

interface IPendingVerificationProjects {
  vendor: NonNullable<IVendorQuery["company"]>;
}

export default function FullVendorProjects({
  vendor,
  showUpgradeBanner = true,
  showPlaceholder = true,
}: IFullVendorProjects) {
  if (vendor.plan.limits.projectsPerMonth === 0 && vendor.viewerIsMember) {
    return showUpgradeBanner ? <UpgradeAccountBanner /> : null;
  }

  return (
    <div>
      <VendorProjects vendor={vendor} showPlaceholder={showPlaceholder} />

      <PendingVerificationProjects vendor={vendor} />

      <RelatedProjects vendor={vendor} />
    </div>
  );
}

function VendorProjects({ vendor, showPlaceholder }: IVendorProjects) {
  const [createProjectModalOpen, setCreateProjectModalOpen] = useState<boolean>(false);
  const intl = useIntl();
  const router = useRouter();

  return (
    <>
      <CreateProjectModal isOpen={createProjectModalOpen} onClose={() => setCreateProjectModalOpen(false)} />

      <div css={{ display: "grid", gridRowGap: 28 }}>
        <Query<IVendorProjectsQuery, IVendorProjectsQueryVariables>
          notifyOnNetworkStatusChange={true}
          query={VendorProjectsQuery}
          variables={{
            slug: vendor.slug,
            cursor: null,
            limit: 6,
          }}
        >
          {({ data, loading, fetchMore }) => {
            if (!data || !data.company || !data.company.projects) {
              return <NoProjectConnectionsBox />;
            }

            const { projects } = data.company;

            if (!projects.items) {
              return <NoProjectConnectionsBox />;
            }

            if (projects.items.length === 0 && vendor.viewerIsMember && showPlaceholder) {
              return (
                <div>
                  <div
                    css={{
                      display: "grid",
                      gridRowGap: 28,
                      "&:not(:first-of-type)": {
                        marginTop: 16,
                      },
                    }}
                  >
                    <NoProjectsBox
                      onAddProject={() => setCreateProjectModalOpen(true)}
                      text={intl.formatMessage({
                        defaultMessage: 'Start showcasing your impact by adding projects to your products.', id: 'aHjadX',
                      })}
                      dataIntercomTarget="Vendor Projects"
                      addButtonIntercomTarget="Vendor Add Project"
                      title={intl.formatMessage({ defaultMessage: 'Share your projects', id: 'k0Ak8L' })}
                      buttonText={intl.formatMessage({ defaultMessage: 'Add Project', id: 'BAZtPp' })}
                    />
                  </div>
                </div>
              );
            }

            return (
              <div
                css={{
                  display: "grid",
                  gridRowGap: 28,
                }}
              >
                {projects.items.length > 0 && (
                  <div>
                    {vendor.viewerIsMember && (
                      <Margin mb={28}>
                        <div>
                          <Button
                            theme="secondary"
                            size="large"
                            onClick={() => setCreateProjectModalOpen(true)}
                            color={palette.lightestGray}
                            textColor={palette.darkGray}
                            css={{
                              background: "#fff",
                              fontSize: 16,
                              display: "inline-flex",
                              alignItems: "center",
                            }}
                          >
                            <PenIcon />
                            <span
                              css={{
                                marginLeft: 4,
                                height: 15,
                              }}
                            >
                              <FormattedMessage defaultMessage="New Projects" id="TFZz3g" />
                            </span>
                          </Button>
                        </div>
                      </Margin>
                    )}

                    <ProjectCards
                      onModalClose={() => {
                        router.push("/vendors/[vendorSlug]/[[...content]]", `/vendors/${data?.company?.slug}/projects`);
                      }}
                      projects={projects.items}
                      gridProps={{
                        columns: [1, 1, 2],
                        spacingY: 6,
                      }}
                    />
                    {data.company.projects.pageInfo?.hasNextPage && !loading && (
                      <Flex justifyContent="center" mt={6}>
                        <ShowMoreButtonWithIcon
                          loading={loading}
                          onClick={() => {
                            if (!projects.pageInfo || !projects.pageInfo.endCursor) {
                              return null;
                            }

                            fetchMore({
                              variables: {
                                slug: vendor.slug,
                                cursor: projects.pageInfo.endCursor,
                                limit: 6,
                              },
                              updateQuery: (previousQueryResult, { fetchMoreResult }) => {
                                if (
                                  !fetchMoreResult ||
                                  !fetchMoreResult.company ||
                                  !fetchMoreResult.company.projects.items ||
                                  !previousQueryResult ||
                                  !previousQueryResult.company ||
                                  !previousQueryResult.company.projects ||
                                  !previousQueryResult.company.projects.items
                                ) {
                                  return previousQueryResult;
                                }

                                return {
                                  ...previousQueryResult,
                                  company: {
                                    ...previousQueryResult.company,
                                    projects: {
                                      ...previousQueryResult.company.projects,
                                      ...fetchMoreResult.company.projects,
                                      items: previousQueryResult.company.projects.items.concat(
                                        fetchMoreResult.company.projects.items,
                                      ),
                                    },
                                  },
                                };
                              },
                            });
                          }}
                        />
                      </Flex>
                    )}
                  </div>
                )}
              </div>
            );
          }}
        </Query>
      </div>
    </>
  );
}

interface IRelatedProjectsProps {
  vendor: NonNullable<IVendorQuery["company"]>;
}

function RelatedProjects({ vendor }: IRelatedProjectsProps) {
  const router = useRouter();
  if (vendor.relatedProjects.length === 0) {
    return null;
  }

  return (
    <div>
      <div>
        <h2
          css={{
            margin: "32px 0 16px 0",
            color: palette.sealBlue,
            fontWeight: 400,
            fontSize: 16,
          }}
        >
          <FormattedMessage defaultMessage="Related Projects" id="jvAQ49" />
        </h2>
      </div>

      <ProjectCards
        onModalClose={() => {
          router.push("/vendors/[vendorSlug]/[[...content]]", `/vendors/${vendor.slug}/projects`);
        }}
        projects={vendor.relatedProjects}
        gridProps={{
          columns: [1, 1, 2],
          spacingY: 6,
        }}
      />
    </div>
  );
}

function PendingVerificationProjects({ vendor }: IPendingVerificationProjects) {
  const router = useRouter();

  return (
    <Query<IVendorPendingVerificationProjectsQuery, IVendorPendingVerificationProjectsQueryVariables>
      query={VendorPendingVerificationProjects}
      variables={{
        slug: vendor.slug,
        cursor: null,
      }}
    >
      {({ data, loading, fetchMore }) => {
        if (!data || !data.company || !data.company.projectsPendingVerification.items) {
          return null;
        }

        const { projectsPendingVerification } = data.company;

        if (!projectsPendingVerification.items || projectsPendingVerification.items.length === 0) {
          return null;
        }

        return (
          <div
            css={{
              display: "grid",
              gridRowGap: 24,
            }}
          >
            <div>
              <div
                css={{
                  margin: "32px 0 16px 0",
                }}
              >
                <h2
                  css={{
                    margin: 0,
                    color: palette.sealBlue,
                    fontWeight: 600,
                    fontSize: 18,
                  }}
                >
                  <FormattedMessage defaultMessage="Pending Verification" id="V5sK5b" />
                </h2>

                <h2
                  css={{
                    color: palette.sealBlue,
                    fontWeight: 400,
                    fontSize: 16,
                  }}
                >
                  <FormattedMessage defaultMessage="Projects will be published after a Govlaunch team member reviews them." id="42OjvQ" />
                </h2>
              </div>

              <ProjectsListing
                projects={projectsPendingVerification.items}
                onModalClose={() => {
                  router.push(`/vendor?vendorSlug=${vendor.slug}&content=projects`, `/vendors/${vendor.slug}/projects`);
                }}
                gridProps={{
                  columns: [1, 2],
                  spacingY: 6,
                }}
                hasNextPage={projectsPendingVerification.pageInfo?.hasNextPage || false}
                onLoadMore={() => {
                  if (projectsPendingVerification.pageInfo && projectsPendingVerification.pageInfo.endCursor) {
                    fetchMore({
                      variables: {
                        cursor: projectsPendingVerification.pageInfo.endCursor,
                      },
                      updateQuery: (previousQueryResult, { fetchMoreResult }) => {
                        if (
                          !previousQueryResult.company ||
                          !previousQueryResult.company.projectsPendingVerification.items ||
                          !fetchMoreResult ||
                          !fetchMoreResult.company ||
                          !fetchMoreResult.company.projectsPendingVerification ||
                          !fetchMoreResult.company.projectsPendingVerification.items
                        ) {
                          return previousQueryResult;
                        }

                        return {
                          ...previousQueryResult,
                          company: {
                            ...previousQueryResult.company,
                            projectsPendingVerification: {
                              ...fetchMoreResult.company.projectsPendingVerification,
                              items: [
                                ...previousQueryResult.company.projectsPendingVerification.items,
                                ...fetchMoreResult.company.projectsPendingVerification.items,
                              ],
                            },
                          },
                        };
                      },
                    });
                  }
                }}
                loading={loading}
              />
            </div>
          </div>
        );
      }}
    </Query>
  );
}
